import {Inject, Injectable} from '@angular/core';
import {ANALYTICS_ACCOUNT_ID, STORE_FRONT} from '../providers/storeFront.providers';
import {Observable, of, ReplaySubject} from 'rxjs';
import {StoreFront} from '../model/store-front';
import {DOCUMENT} from '@angular/common';
import {filter, map, take} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {startTracking} from '../analytics/analytics.actions';
import {WindowService} from '../window/window.service';
import {DialogOptions} from '../window/window.model';
import {ComponentsIds} from './templates.service';

export const luneShopCookie = 'lune_shop_consent';

export type LuneShopCookiesStatus = 'wait' | 'true' | 'false';

export interface LuneShopCookies {
  [serviceId: string]: LuneShopCookiesStatus;
}

@Injectable({
  providedIn: 'root'
})
export class TarteAuCitronService {

  cookies$: ReplaySubject<{ [cookieId: string]: string }> = new ReplaySubject<{ [cookieId: string]: string }>(1);

  luneshopCookies$: ReplaySubject<LuneShopCookies> = new ReplaySubject<LuneShopCookies>(1);

  hasChoosen$: Observable<boolean>;

  hasAccepted$: Observable<boolean>;

  showCookieBanner$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  services: string[] = ['youtubeapi', 'gtag'];

  privacyPolicyPath$: Observable<string>;

  enableConsentManagement$: Observable<boolean>;

  constructor(
    @Inject(ANALYTICS_ACCOUNT_ID) private analyticsAccountId$: Observable<string>,
    @Inject(STORE_FRONT) private storeFront$: Observable<StoreFront>,
    @Inject(DOCUMENT) private document: Document,
    private store: Store<any>,
    private windowService: WindowService
  ) {
  }

  public init() {

    // this.setCookie('lune_shop', '!youtubeapi:true!gtag:true', 365);

    this.store.dispatch(startTracking());

    this.privacyPolicyPath$ = this.storeFront$.pipe(
      map(storeFront => {
        return storeFront?.privacyPolicyPath;
      })
    );

    this.enableConsentManagement$ = this.storeFront$.pipe(
      filter(storeFront => !!storeFront),
      map(storeFront => {
        return storeFront?.enableConsentManagement;
      })
    );

    this.enableConsentManagement$.pipe(
      take(1)
    ).subscribe(enableConsentManagement => {

      // console.log('enableConsentManagement', enableConsentManagement);

      if (enableConsentManagement) {

        this.readCookies();

        this.cookies$
          .subscribe(cookies => {
            const hasCookie: boolean = cookies.hasOwnProperty(luneShopCookie);
            if (hasCookie) {
              const luneShopRawCookies = cookies[luneShopCookie];
              const luneShopRawCookiesSplit: string[] = luneShopRawCookies.split('!').filter(rawCookie => rawCookie !== '');
              const luneShopCookies: LuneShopCookies = {};
              luneShopRawCookiesSplit.forEach(rawCookie => {
                const rawCookieSplit: string[] = rawCookie.split(':');
                if (rawCookieSplit.length === 2) {
                  const key: string = rawCookieSplit[0];
                  const value: LuneShopCookiesStatus = rawCookieSplit[1] as LuneShopCookiesStatus;
                  luneShopCookies[key] = value;
                }
              });
              this.luneshopCookies$.next(luneShopCookies);
            } else {
              this.buildCookie('wait');
              this.showCookieBanner$.next(true);
            }
          });

        this.hasChoosen$ = this.luneshopCookies$.pipe(
          filter(luneshopCookies => !!luneshopCookies),
          map(luneshopCookies => {
            let hasChoosen = true;
            Object.keys(luneshopCookies).forEach(key => {
              if (luneshopCookies[key] === 'wait') {
                hasChoosen = false;
              }
            });
            return hasChoosen;
          })
        );

        this.hasAccepted$ = this.luneshopCookies$.pipe(
          map(luneshopCookies => {
            if (luneshopCookies) {
              let hasAccepted = true;
              Object.keys(luneshopCookies).forEach(key => {
                if (luneshopCookies[key] === 'false' || luneshopCookies[key] === 'wait') {
                  hasAccepted = false;
                }
              });
              return hasAccepted;
            } else {
              return false;
            }
          })
        );

        this.hasChoosen$.subscribe(hasChoosen => {
          // console.log('hasChoosen', hasChoosen);
          this.showCookieBanner$.next(!hasChoosen);
        });

        this.hasAccepted$
          .pipe(
            filter(hasAccepted => hasAccepted === true),
            take(1)
          )
          .subscribe(hasAccepted => {
            this.enableServices();
          });

      } else {
        this.hasAccepted$ = of(true);
        this.enableServices();
      }

    });

    this.showCookieBanner$
      .pipe(
        filter(showCookieBanner => showCookieBanner === true),
        take(1)
      )
      .subscribe(showCookieBanner => {
        const dialogOptions: DialogOptions = {
          passport: {
            windowComponent: ComponentsIds.cookieBanner
          },
          dialogConfig: {
            panelClass: 'cookie-banner-overlay',
            position: {top: '0px'},
            disableClose: true,
            backdropClass: 'transparent'
          }
        };
        this.windowService.openWindow(dialogOptions);
      });

  }

  public checkAccepted(): void {
    if (this.hasAccepted$) {
      this.hasAccepted$
        .pipe(
          filter(hasAccepted => hasAccepted === true),
          take(1)
        )
        .subscribe(hasAccepted => {
          this.enableServices();
        });
    }
  }

  public serverEnable() {
    this.hasAccepted$ = of(true);
  }

  private enableServices() {
    if (typeof (YT) === 'undefined' || typeof (YT.Player) === 'undefined') {
      const tag = this.document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      this.document.body.appendChild(tag);
    }
  }

  private readCookies() {

    const rawCookies: string[] = this.document?.cookie?.split('; ');
    const cookies: { [cookieId: string]: string } = {};
    rawCookies.forEach(rawCookie => {
      const rawCookieSplit: string[] = rawCookie.split('=');
      if (rawCookieSplit.length === 2) {
        const key: string = rawCookieSplit[0];
        const value: string = rawCookieSplit[1];
        cookies[key] = value;
      }
    });

    this.cookies$.next(cookies);

  }

  private setCookie(name: string, value: string, expireDays: number, path: string = '') {
    const d: Date = new Date();
    d.setTime(d.getTime() + expireDays * 24 * 60 * 60 * 1000);
    const expires = `expires=${d.toUTCString()}`;
    const cpath: string = path ? `; path=${path}` : '';
    this.document.cookie = `${name}=${value}; ${expires}${cpath}`;
  }

  public acceptAll() {
    this.buildCookie('true');
  }

  public denyAll() {
    this.buildCookie('false');
    /*this.setCookie('_ga', '', 0);
    this.setCookie('_gid', '', 0);
    this.setCookie('VISITOR_INFO1_LIVE', '', 0);*/
  }

  private buildCookie(status: LuneShopCookiesStatus) {
    let cookieValue = '';
    this.services.forEach(service => {
      cookieValue += '!' + service + ':' + status;
    });
    this.setCookie(luneShopCookie, cookieValue, 365, '/');
    this.readCookies();
  }


}
